import { useEffect, useMemo } from 'react';
import type { Company } from '@packages/types/company/entities';
import type { SiteResponse } from '@packages/types/site/dto';
import { useAuthStore } from '@/store/authStore';
import { useSelectionStore } from '@/store/selectionStore';
import { isAdminByRoleId, isSiteManagerByRoleId, isCompanyManagerByRoleId, resolveRoleId } from '@packages/types/auth/roles';
import {
  useGetAllCompanies,
  useGetUserCompany,
  useGetSitesByCompanyId,
  useGetMySites,
} from '@/api/admin/useCompanyManagement';

interface CompanySiteScopeResult {
  companies: Company[];
  currentCompany: Company | null;
  sites: SiteResponse[];
  mySite: SiteResponse | null | undefined;
  currentSiteId: number | null;
  isAdmin: boolean;
  isCompanyManager: boolean;
  isSiteManager: boolean;
  isLoading: boolean;
  loadingSites: boolean;
}

/**
 * 회사/사이트 범위를 역할 기반으로 결정하는 공통 훅
 *
 * - Admin: 전체 회사 → 선택한 회사의 사이트
 * - CompanyManager: 자기 회사 → 회사 내 사이트
 * - SiteManager: 자기 회사 → 자기 담당 사이트만
 */
export function useCompanySiteScope(): CompanySiteScopeResult {
  const { user, isAuthenticated } = useAuthStore();
  const { selectedCompanyId, selectedSiteId, setSelectedSiteId } = useSelectionStore();

  const roleId = user ? resolveRoleId(user) : undefined;

  const isAdmin = roleId !== undefined && isAdminByRoleId(roleId);
  const isCompanyManager = roleId !== undefined && isCompanyManagerByRoleId(roleId);
  const isSiteManager = roleId !== undefined && isSiteManagerByRoleId(roleId);
  // 순수 SiteManager(role_id=3)만: CompanyManager 이상은 제외
  const isSiteManagerOnly = isSiteManager && !isCompanyManager;

  // Company 조회
  const { data: allCompanies = [], isLoading: loadingAllCompanies } = useGetAllCompanies({
    enabled: isAuthenticated && isAdmin,
  });
  const { data: myCompany, isLoading: loadingMyCompany } = useGetUserCompany({
    enabled: isAuthenticated && !isAdmin && !!user?.company_id,
  });

  const companies = useMemo(() => {
    if (isAdmin) {
      return allCompanies.filter((c: Company) => c.status === 'active');
    }
    return myCompany && myCompany.status === 'active' ? [myCompany] : [];
  }, [isAdmin, allCompanies, myCompany]);

  const currentCompany = useMemo(() => {
    if (isAdmin) {
      return selectedCompanyId && selectedCompanyId !== 'all'
        ? companies.find((c: Company) => c.id === selectedCompanyId) ?? null
        : null;
    }
    return myCompany && myCompany.status === 'active' ? myCompany : null;
  }, [isAdmin, selectedCompanyId, companies, myCompany]);

  // Site 조회
  // Admin/CompanyManager: 회사 전체 사이트 (site_manager_name 포함)
  // SiteManager(only): 자기 담당 사이트만
  const { data: allSitesInCompany = [], isLoading: loadingAllSites } = useGetSitesByCompanyId(
    currentCompany?.id || 0,
    { enabled: !!currentCompany?.id && !isSiteManagerOnly },
  );
  const { data: mySitesRaw = [], isLoading: loadingMySites } = useGetMySites({
    enabled: isSiteManagerOnly,
  });

  const mySites = useMemo(
    () => mySitesRaw.filter((s: SiteResponse) => s.status === 'active'),
    [mySitesRaw],
  );

  const sites = useMemo(() => {
    if (isSiteManagerOnly) return mySites;
    return allSitesInCompany.filter((s: SiteResponse) => s.status === 'active');
  }, [isSiteManagerOnly, mySites, allSitesInCompany]);

  // backward compat: 첫 번째 사이트 (단일 사이트 케이스용)
  const mySite = mySites[0] ?? null;

  const currentSiteId = useMemo(() => {
    if (isSiteManagerOnly) {
      // 단일 사이트 → 자동으로 해당 ID
      if (mySites.length === 1) return mySites[0].id;
      // 다중 사이트 → selectedSiteId 따름 ('all'이면 null)
    }
    if (selectedSiteId && selectedSiteId !== 'all') {
      return selectedSiteId as number;
    }
    return null;
  }, [isSiteManagerOnly, mySites, selectedSiteId]);

  const isLoading = isAdmin ? loadingAllCompanies : loadingMyCompany;
  const loadingSites = isSiteManagerOnly ? loadingMySites : loadingAllSites;

  // SiteManager 전용: persist된 선택이 담당 사이트 밖이면 보정 (loadingSites는 위에서 계산된 뒤 사용)
  useEffect(() => {
    if (!isSiteManagerOnly || loadingSites) return;
    if (selectedSiteId === null || selectedSiteId === 'all') return;
    const id = selectedSiteId as number;
    if (sites.some((s: SiteResponse) => s.id === id)) return;
    setSelectedSiteId(sites[0]?.id ?? null);
  }, [isSiteManagerOnly, loadingSites, sites, selectedSiteId, setSelectedSiteId]);

  return {
    companies,
    currentCompany,
    sites,
    mySite,
    currentSiteId,
    isAdmin,
    isCompanyManager,
    isSiteManager,
    isLoading,
    loadingSites,
  };
}
